import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

type CanvasFocusOptions = {
  active: boolean;
  targetKey: string;
  autoOpened: boolean;
  heading: RefObject<HTMLElement | null>;
  canvas: RefObject<HTMLElement | null>;
  onClose: () => unknown;
};

const focusExcluded =
  'input, textarea, select, [contenteditable="true"], [role="dialog"], [aria-modal="true"], [data-radix-popper-content-wrapper]';

function canFocus(element: HTMLElement | null): element is HTMLElement {
  if (!element?.isConnected) return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    if (
      current.hasAttribute('inert') ||
      current.hasAttribute('hidden') ||
      current.getAttribute('aria-hidden') === 'true'
    )
      return false;
    const style = current.ownerDocument.defaultView?.getComputedStyle(current);
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
  }
  return true;
}

/** Escape is owned by an open desktop canvas, except inside another modal/control. */
export function isCanvasEscape(event: KeyboardEvent, canvas: HTMLElement | null): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented || !canvas) return false;
  const target = event.target as Element | null;
  if (!target || !canvas.contains(target)) return false;
  const excluded = target.closest(focusExcluded);
  if (excluded && excluded !== canvas) return false;
  return true;
}

/** Keep the opener above the keyed canvas, which unmounts on close/replacement. */
export function useCanvasReturnFocus(
  active: boolean,
  autoOpened: boolean,
  shell: RefObject<HTMLElement | null>,
): void {
  const wasActive = useRef(false);
  const origin = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (active && !wasActive.current && !autoOpened) {
      const focused = document.activeElement as HTMLElement | null;
      origin.current =
        focused && shell.current?.contains(focused) && !focused.closest('.ws-canvas')
          ? focused
          : null;
    }
    if (!active && wasActive.current) {
      const returnTarget = origin.current;
      origin.current = null;
      queueMicrotask(() => {
        // Modality may already have restored focus, or the person may have
        // chosen another control. Only recover focus lost with the canvas.
        if (document.activeElement !== document.body) return;
        if (canFocus(returnTarget)) return returnTarget.focus();
        // Reached by link or reload: there is no opener, so land on the page heading.
        const heading = [
          ...(shell.current?.querySelectorAll<HTMLElement>('.ws-main-column h1') ?? []),
        ].find((candidate) => !candidate.closest('.ws-canvas') && canFocus(candidate));
        // Chat has no page heading: land where the skip link does, never on the composer.
        const target =
          heading ??
          [
            ...(shell.current?.querySelectorAll<HTMLElement>('.ws-chat-slot, .ws-page-scroll') ??
              []),
          ].find(canFocus);
        if (target) {
          if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
          target.focus();
        }
      });
    }
    wasActive.current = active;
  }, [active, autoOpened, shell]);
}

/** Initial focus and desktop Escape. Modality owns the focus trap. */
export function useCanvasFocus({
  active,
  targetKey,
  autoOpened,
  heading,
  canvas,
  onClose,
}: CanvasFocusOptions): void {
  const previous = useRef({ active: false, targetKey: '' });
  useLayoutEffect(() => {
    const wasActive = previous.current.active;
    if (active && !autoOpened && (!wasActive || previous.current.targetKey !== targetKey)) {
      queueMicrotask(() => heading.current?.focus());
    }
    previous.current = { active, targetKey };
  }, [active, autoOpened, canvas, heading, targetKey]);

  useEffect(() => {
    if (!active) return;
    const key = (event: KeyboardEvent) => {
      const narrow =
        typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 640px)').matches;
      if (!narrow && isCanvasEscape(event, canvas.current)) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [active, canvas, onClose]);
}
